/**
 * EDIT COVER — the owner's cover controls on the Pro page (design_handoff_pro_page/README.md
 * "Cover editor"). Mirrors the host's step-5 COVER & PREVIEW: how many photos, their order,
 * the layout, split width, swap / reverse, drag-to-reframe (on the cover above), which photo
 * the People → Pros card shows, plus the cover video.
 *
 * The page owns the cover document (`cover` / `setCover`); this panel edits it live and saves
 * through `PUT /pro/pages/{id}/cover` (debounced). Adding photos uploads them, appends them on
 * the server and asks the page to reload so the new rows come back with their ids.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import proService from '../../services/proService';
import CoverVideoField, { proCoverVideoApi } from './CoverVideoField';
import { autoLayout, validLayouts, resolveLayout, coverGeom, layoutNote, isPortrait, focalPosition, LAYOUT_META, SPLITS } from '../event/coverLayout';
import { RING_ON } from '../surface/SurfaceStyleContext';
import { coverSlice, HOT_PINK } from './proPageModel';

const SAVE_MS = 700;

/** The Pro page's cover endpoints. A club passes `clubCoverApi(clubId)` (components/club/clubCoverModel). */
export const proCoverApi = (pageId) => ({
  updateCover: (payload) => proService.updateCover(pageId, payload),
  uploadAsset: (file) => proService.uploadAsset(file),
  video: proCoverVideoApi(pageId),
});

const PRO_COPY = {
  intro: 'Your logo and name sit on the left. Drag any photo on the cover to choose what stays in frame.',
  cardNote: 'Order sets which photo goes where. The CARD photo is the one people see under People → Pros.',
  pageWord: 'page',
};
const label = { fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase' };
const small = { fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase' };
const help = { fontSize: 11, lineHeight: 1.6, color: '#6B7280' };
const pillBtn = (on, extra = {}) => ({
  height: 38, padding: '0 16px', border: 'none', borderRadius: 999, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', cursor: 'pointer', textTransform: 'uppercase',
  background: on ? '#000' : '#F2F2F2', color: on ? '#fff' : '#000', ...extra,
});

function LayoutThumb({ layoutKey, photos, split, on, suggested, logoBg, onClick }) {
  const g = coverGeom(layoutKey, photos, 1216, 540, false, split);
  const meta = LAYOUT_META[layoutKey];
  return (
    <button type="button" onClick={onClick} aria-pressed={on} data-testid={`cover-layout-${layoutKey}`}
      style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 10, border: '2px solid transparent', borderRadius: 20, background: on ? RING_ON : '#F5F5F5', color: on ? '#fff' : '#000', fontFamily: 'inherit', textAlign: 'left', cursor: 'pointer', transition: 'background-color .2s, color .2s' }}>
      <span style={{ position: 'relative', display: 'block', width: '100%', aspectRatio: '2.35', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
        {g.tiles.map((t) => {
          const p = photos[t.i] || photos[0];
          return (
            <span key={`${t.i}-${t.x}-${t.y}`} style={{ position: 'absolute', left: `${t.x}%`, top: `${t.y}%`, width: `${t.w}%`, height: `${t.h}%`, padding: `${t.y > 0.5 ? 2 : 0}px 0 0 ${t.x > 0.5 ? 2 : 0}px`, boxSizing: 'border-box', background: '#fff' }}>
              <img src={p?.src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: focalPosition(p), display: 'block' }} />
            </span>
          );
        })}
        {g.fadeW > 0 && <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${g.fadeW}%`, background: 'linear-gradient(90deg,#fff 0%,rgba(255,255,255,.95) 48%,rgba(255,255,255,0) 100%)' }} />}
        <span style={{ position: 'absolute', left: '7%', top: '18%', width: `${g.textW * 0.8}%`, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ display: 'block', width: '18%', aspectRatio: '1', borderRadius: 3, background: logoBg }} />
          <span style={{ display: 'block', width: '30%', height: 4, borderRadius: 2, background: HOT_PINK }} />
          <span style={{ display: 'block', width: '80%', height: 6, borderRadius: 2, background: '#000' }} />
          <span style={{ display: 'block', width: '46%', height: 4, borderRadius: 2, background: '#9CA3AF' }} />
        </span>
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '0 4px 2px' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ ...label, letterSpacing: '0.14em' }}>{meta.name}</span>
          {suggested && <span style={{ padding: '2px 7px', borderRadius: 999, background: '#00FFFF', color: '#000', fontSize: 7, fontWeight: 800, letterSpacing: '0.14em' }}>SUGGESTED</span>}
        </span>
        <span style={{ fontSize: 10, lineHeight: 1.5, opacity: 0.7 }}>{meta.hint}</span>
      </span>
    </button>
  );
}

export default function ProCoverEditor({ pageId, cover, setCover, video, onVideoChange, onReload, onClose, phone = false, hasLogo = false, api: apiProp = null, copy: copyProp = null, canRemove = false, testId = 'pro-cover-editor', showVideo = true }) {
  const api = useMemo(() => apiProp || proCoverApi(pageId), [apiProp, pageId]);
  const copy = { ...PRO_COPY, ...(copyProp || {}) };
  const fileRef = useRef(null);
  const [adding, setAdding] = useState(false);
  const [saveState, setSaveState] = useState('idle');

  // ── save (debounced) ─────────────────────────────────────────────────────────
  const pendingRef = useRef({});
  const timerRef = useRef(null);
  const flushRef = useRef(null);
  const queueSave = useCallback((patch) => {
    if (!api) return;
    pendingRef.current = { ...pendingRef.current, ...patch };
    window.clearTimeout(timerRef.current);
    setSaveState('saving');
    const flush = async () => {
      timerRef.current = null;
      const payload = pendingRef.current;
      pendingRef.current = {};
      const r = await api.updateCover(payload);
      setSaveState(r?.success ? 'saved' : 'error');
    };
    flushRef.current = flush;
    timerRef.current = window.setTimeout(flush, SAVE_MS);
  }, [api]);
  // DONE / × within SAVE_MS of a pick used to drop the save with the timer; send it on the way out.
  useEffect(() => () => {
    if (timerRef.current == null) return;
    window.clearTimeout(timerRef.current);
    flushRef.current?.();
  }, []);

  const photos = cover.photos;
  const n = photos.length;
  const inCover = coverSlice(cover);
  const k = inCover.length;
  const valid = validLayouts(k);
  const suggested = autoLayout(inCover);
  const layoutKey = resolveLayout(cover.layout, inCover);
  const card = photos.find((p) => p.id === cover.cardId) || photos[0] || null;

  const patch = (next, save) => { setCover((c) => ({ ...c, ...next })); if (save) queueSave(save); };
  const setOrder = (list) => patch({ photos: list }, { order: list.map((p) => p.id) });
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= n) return;
    const next = [...photos];
    [next[i], next[j]] = [next[j], next[i]];
    setOrder(next);
  };
  const pickCount = (c) => patch({ count: c, layout: 'auto' }, { photoCount: c, layout: 'auto' });
  const pickLayout = (l) => patch({ layout: l }, { layout: l });
  const pickSplit = (v) => patch({ split: v }, { split: v });
  const reverse = () => setOrder([...photos.slice(0, k).reverse(), ...photos.slice(k)]);
  const swap = () => move(0, 1);
  const resetFraming = () => {
    const next = photos.map((p) => ({ ...p, fx: 50, fy: 50 }));
    patch({ photos: next }, { focal: next.map((p) => ({ id: p.id, x: 50, y: 50 })) });
  };
  const pickCard = (p) => patch({ cardId: p.id }, { cardId: p.id });
  // ✕ on a tray tile (clubs): the photo leaves the pool at once; the card pick moves on if it was this one.
  const removePhoto = (p) => {
    const next = photos.filter((x) => x.id !== p.id);
    patch({ photos: next, cardId: cover.cardId === p.id ? null : cover.cardId, count: cover.count > next.length ? 0 : cover.count }, { remove: [p.id] });
  };

  const addFiles = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setAdding(true);
    const paths = [];
    for (const f of list) {
      // eslint-disable-next-line no-await-in-loop
      const r = await api.uploadAsset(f);
      if (r?.success && r.data?.path) paths.push(r.data.path);
    }
    if (paths.length) {
      const r = await api.updateCover({ addPaths: paths });
      setSaveState(r?.success ? 'saved' : 'error');
      if (r?.success) await onReload?.();
    }
    setAdding(false);
  };

  const countTag = n ? `${k} OF ${n} PHOTOS IN THE COVER` : 'NO PHOTOS YET';
  const saveNote = saveState === 'saving' ? 'SAVING…' : saveState === 'saved' ? 'SAVED' : saveState === 'error' ? 'NOT SAVED — TRY AGAIN' : '';

  return (
    <section className="flex flex-col bg-white font-mono" style={{ borderRadius: phone ? 22 : 24, padding: phone ? '18px 16px' : '24px 26px', gap: 22 }} data-testid={testId}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14 }}>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.16em' }}>COVER</span>
          <span style={{ fontSize: 12, lineHeight: 1.55, color: '#6B7280' }}>{copy.intro}</span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {saveNote && <span style={{ ...small, color: saveState === 'error' ? '#DC2626' : '#9CA3AF' }} data-testid="cover-save-state">{saveNote}</span>}
          <span style={{ padding: '5px 10px', borderRadius: 999, background: '#F2F2F2', ...small }} data-testid="cover-count-tag">{countTag}</span>
          <button type="button" onClick={onClose} style={{ height: 38, padding: '0 18px', border: 'none', borderRadius: 999, background: '#000', color: '#fff', fontFamily: 'inherit', fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', cursor: 'pointer' }} data-testid="cover-done">DONE</button>
        </span>
      </div>

      {/* ── PHOTOS ─────────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <span style={label}>PHOTOS</span>
          {n > 0 && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ ...small, color: '#6B7280' }}>IN THE COVER</span>
              <span role="radiogroup" aria-label="Photos in the cover" style={{ display: 'flex', gap: 2, padding: 3, borderRadius: 999, background: '#F5F5F5' }}>
                {photos.map((_, i) => (
                  <button key={i} type="button" role="radio" aria-checked={i + 1 === k} onClick={() => pickCount(i + 1)} data-testid={`cover-count-${i + 1}`}
                    style={{ width: 32, height: 30, border: '2px solid transparent', borderRadius: 999, background: i + 1 === k ? '#000' : 'transparent', color: i + 1 === k ? '#fff' : '#000', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                    {i + 1}
                  </button>
                ))}
              </span>
            </span>
          )}
        </span>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(112px, 1fr))', gap: 10 }} data-testid="cover-tray">
          {photos.map((p, i) => {
            const on = i < k;
            const isCard = card?.id === p.id;
            return (
              <div key={p.id} style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }} data-testid="cover-tray-tile" data-photo={p.id} data-in-cover={on ? '1' : '0'}>
                <div style={{ position: 'relative', aspectRatio: '1', borderRadius: 16, overflow: 'hidden', background: '#111', opacity: on ? 1 : 0.4, border: `2px solid ${isCard ? HOT_PINK : '#fff'}`, boxSizing: 'border-box' }}>
                  <img src={p.src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: focalPosition(p), display: 'block' }} />
                  <span style={{ position: 'absolute', left: 6, top: 6, minWidth: 22, height: 22, padding: '0 6px', boxSizing: 'border-box', borderRadius: 999, background: '#000', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800 }}>{i + 1}</span>
                  {i === 0 && video && <span style={{ position: 'absolute', right: 6, bottom: 6, padding: '4px 8px', borderRadius: 999, background: '#000', color: '#fff', fontSize: 8, fontWeight: 700, letterSpacing: '0.14em' }}>POSTER</span>}
                  {isCard && <span style={{ position: 'absolute', right: 6, top: 6, padding: '4px 8px', borderRadius: 999, background: HOT_PINK, color: '#fff', fontSize: 8, fontWeight: 700, letterSpacing: '0.14em' }}>CARD</span>}
                  <span style={{ position: 'absolute', left: 6, bottom: 6, display: 'flex', gap: 4 }}>
                    <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move photo ${i + 1} earlier`} style={{ width: 26, height: 26, border: 'none', borderRadius: '50%', background: '#fff', fontSize: 10, cursor: 'pointer', opacity: i === 0 ? 0.35 : 1 }}>◄</button>
                    <button type="button" onClick={() => move(i, 1)} disabled={i === n - 1} aria-label={`Move photo ${i + 1} later`} style={{ width: 26, height: 26, border: 'none', borderRadius: '50%', background: '#fff', fontSize: 10, cursor: 'pointer', opacity: i === n - 1 ? 0.35 : 1 }}>►</button>
                  </span>
                </div>
                <span style={{ ...small, fontSize: 9, letterSpacing: '0.12em', color: on ? '#000' : '#9CA3AF' }}>{on ? 'COVER' : 'NOT IN COVER'} · {isPortrait(p) ? 'PORTRAIT' : 'LANDSCAPE'}</span>
                <span style={{ display: 'flex', gap: 4 }}>
                  <button type="button" onClick={() => pickCard(p)} disabled={isCard} data-testid={`cover-card-pick-${p.id}`}
                    style={{ flex: 1, minWidth: 0, height: 28, border: 'none', borderRadius: 999, background: isCard ? HOT_PINK : '#F2F2F2', color: isCard ? '#fff' : '#000', fontFamily: 'inherit', fontSize: 8, fontWeight: 700, letterSpacing: '0.12em', cursor: isCard ? 'default' : 'pointer' }}>
                    {isCard ? 'ON THE CARD' : 'USE FOR CARD'}
                  </button>
                  {canRemove && (
                    <button type="button" onClick={() => removePhoto(p)} aria-label={`Remove photo ${i + 1}`} data-testid={`cover-remove-${p.id}`}
                      style={{ flexShrink: 0, width: 28, height: 28, border: 'none', borderRadius: '50%', background: '#F2F2F2', color: '#DC2626', fontFamily: 'inherit', fontSize: 11, fontWeight: 800, cursor: 'pointer' }}>✕</button>
                  )}
                </span>
              </div>
            );
          })}
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-testid="cover-add-input" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={adding} data-testid="cover-add"
            style={{ aspectRatio: '1', border: '2px dashed #D1D5DB', borderRadius: 16, background: '#F5F5F5', fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: '#6B7280', cursor: 'pointer', opacity: adding ? 0.5 : 1 }}>
            {adding ? 'ADDING…' : '+ ADD'}
          </button>
        </div>
        <span style={help}>{copy.cardNote}</span>
      </div>

      {/* ── COVER VIDEO (gatherings have none: showVideo=false) ────────────────── */}
      {showVideo && api.video && <CoverVideoField soft pageId={pageId} api={api.video} pageWord={copy.pageWord} initial={video ?? null} onChange={onVideoChange} />}

      {/* ── COVER LAYOUT ───────────────────────────────────────────────────────── */}
      {k > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <span style={label}>COVER LAYOUT</span>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 8 }} data-testid="cover-layouts">
            {valid.map((key) => (
              <LayoutThumb key={key} layoutKey={key} photos={inCover} split={cover.split} on={key === layoutKey} suggested={key === suggested} logoBg={hasLogo ? '#222' : '#E5E7EB'} onClick={() => pickLayout(key)} />
            ))}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            {layoutKey === 'split' && (
              <>
                <span style={{ ...small, color: '#6B7280' }}>WIDTH</span>
                <span role="radiogroup" aria-label="Split width" style={{ display: 'flex', gap: 2, padding: 3, borderRadius: 999, background: '#F5F5F5' }}>
                  {SPLITS.map((v) => (
                    <button key={v} type="button" role="radio" aria-checked={cover.split === v} onClick={() => pickSplit(v)} data-testid={`cover-split-${v}`}
                      style={{ height: 32, padding: '0 12px', border: '2px solid transparent', borderRadius: 999, background: cover.split === v ? '#000' : 'transparent', color: cover.split === v ? '#fff' : '#000', fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', cursor: 'pointer' }}>
                      {v}/{100 - v}
                    </button>
                  ))}
                </span>
                <button type="button" onClick={swap} style={pillBtn(true)} data-testid="cover-swap">⇄ SWAP SIDES</button>
              </>
            )}
            {(layoutKey === 'stack' || layoutKey === 'row') && k > 1 && (
              <button type="button" onClick={reverse} style={pillBtn(true)} data-testid="cover-reverse">{layoutKey === 'stack' ? '⇅ REVERSE ORDER' : '⇄ REVERSE ORDER'}</button>
            )}
            <span style={{ flex: '1 1 200px', minWidth: 0, fontSize: 11, lineHeight: 1.55, color: '#6B7280' }} data-testid="cover-layout-note">{layoutNote(layoutKey, cover.split, n)}</span>
            <button type="button" onClick={resetFraming} style={pillBtn(false)} data-testid="cover-reset-framing">RESET FRAMING</button>
          </div>
        </div>
      )}
    </section>
  );
}
